import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { Link } from "@tanstack/react-router";
import type { Row } from "@tanstack/react-table";
import {
  CheckCircle2,
  CircleDot,
  Edit,
  Loader2,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { CanOrg } from "@/app/capabilities/components/can-org";
import { ORG } from "@/app/capabilities/permissions";
import { InviteMemberModal } from "@/app/invitations/components/modals/invite-member-modal";
import { ORG_PROJECT_ROUTE_PATH } from "@/lib/org-project-context/constants";
import {
  useDashboardOrgProject,
  useOrgProjectRouteContext,
} from "@/lib/org-project-context/hooks";
import { useDeleteProject } from "../api/hooks";
import { UpdateProjectModal } from "../components/update-project-modal";
import type { Project } from "../types";

const DeleteProjectButton = ({ project }: { project: Project }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { organizationId } = useOrgProjectRouteContext();
  const { mutate: deleteProject, isPending } = useDeleteProject(organizationId);
  const { activeProjectId } = useDashboardOrgProject();

  const isActive = activeProjectId === project.project_id;
  const isDisabled = isPending || isActive || project.is_default;

  const handleDelete = () => {
    deleteProject(project.project_id);
    setIsOpen(false);
  };

  const tooltipMessage = isActive
    ? "You cannot delete the active project. Switch to another project first."
    : "The default project cannot be deleted as it is required for your workspace.";

  const button = (
    <Button
      className="gap-1.5 border-destructive/30 text-destructive transition-all hover:border-destructive hover:bg-destructive hover:text-destructive-foreground hover:shadow-sm disabled:opacity-50 md:gap-1.5"
      disabled={isDisabled}
      size="sm"
      variant="outline"
    >
      {isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Trash2 className="size-4" />
      )}
      <span className="hidden text-xs md:inline">Delete</span>
    </Button>
  );

  const trigger = isDisabled ? (
    <Tooltip>
      <TooltipTrigger render={<span className="cursor-not-allowed" />}>
        <PopoverTrigger disabled render={button} />
      </TooltipTrigger>
      <TooltipContent>
        <span className="text-sm">{tooltipMessage}</span>
      </TooltipContent>
    </Tooltip>
  ) : (
    <PopoverTrigger render={button} />
  );

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      {trigger}
      <PopoverContent className="w-80">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">Delete Project</h4>
            <p className="text-muted-foreground text-sm">
              Are you sure you want to delete {project.name}? This action cannot
              be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setIsOpen(false)}
              size="sm"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              className="disabled:cursor-not-allowed"
              disabled={isPending}
              onClick={handleDelete}
              size="sm"
              variant="destructive"
            >
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const EditProjectButton = ({ project }: { project: Project }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger
        render={
          <Button
            className="gap-1.5 transition-all hover:shadow-sm md:gap-1.5"
            size="sm"
            title="Edit project"
            variant="outline"
          />
        }
      >
        <Edit className="size-4" />
        <span className="hidden text-xs md:inline">Edit</span>
      </DialogTrigger>
      <UpdateProjectModal project={project} setIsOpen={setIsOpen} />
    </Dialog>
  );
};

const InviteMemberButton = ({ project }: { project: Project }) => (
  <InviteMemberModal
    project={project}
    trigger={
      <Button
        className="gap-1.5 transition-all hover:shadow-sm md:gap-1.5"
        size="sm"
        title="Invite member"
        variant="outline"
      >
        <UserPlus className="size-4" />
        <span className="hidden text-xs md:inline">Invite</span>
      </Button>
    }
  />
);

const SetActiveProjectButton = ({ project }: { project: Project }) => {
  const { activeProjectId } = useDashboardOrgProject();
  const { organizationId } = useOrgProjectRouteContext();
  const isActive = activeProjectId === project.project_id;

  if (isActive) {
    return (
      <div className="flex items-center gap-1.5 rounded-md bg-green-500/10 px-2.5 py-1.5 text-green-600 dark:text-green-400">
        <CheckCircle2 className="size-3.5" />
        <span className="font-medium text-xs">Active</span>
      </div>
    );
  }

  return (
    <Button
      aria-label="Set as active project"
      className="gap-1.5 hover:bg-secondary hover:text-secondary-foreground"
      render={
        <Link
          from={ORG_PROJECT_ROUTE_PATH}
          params={{ organizationId, projectId: project.project_id }}
          search={(prev: Record<string, unknown>) => prev}
          to="./projects"
        />
      }
      size="sm"
      variant="ghost"
    >
      <CircleDot className="size-4" />
      <span className="text-xs">Set Active</span>
    </Button>
  );
};

export function DataTableRowActions({ row }: { row: Row<Project> }) {
  const project = row.original;

  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-1">
        {project.can_invite && <InviteMemberButton project={project} />}
        {project.can_edit && <EditProjectButton project={project} />}
        <CanOrg permission={ORG.projects.delete}>
          <DeleteProjectButton project={project} />
        </CanOrg>
      </div>
      <div className="mx-1 h-4 w-px bg-border" />
      <SetActiveProjectButton project={project} />
    </div>
  );
}
